import { useEffect, lazy, Suspense } from 'react'
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { setUnauthorizedHandler, push } from './api.js'
import { ensurePushSubscription } from './push.js'
import { Screen, ScreenTitle } from './ui/Screen.jsx'
import { BatteryBoundary } from './ui/BatteryBoundary.jsx'

/**
 * Throws on render, exactly as a real component defect does. Used only by the
 * dev-only /dev/crash route below, which is how the boundary is proven to catch
 * rather than assumed to.
 */
function CrashOnRender() {
  throw new Error('dev: deliberate render crash, proving BatteryBoundary catches')
}

import Register from './screens/Register.jsx'
import Login from './screens/Login.jsx'
import Consent from './screens/Consent.jsx'
import Install from './screens/Install.jsx'
import Notifications from './screens/Notifications.jsx'
import Demographics from './screens/Demographics.jsx'
import Day0 from './screens/Day0.jsx'
import Beep from './screens/Beep.jsx'
import Home from './screens/Home.jsx'
import Settings from './screens/Settings.jsx'
import Withdrawn from './screens/Withdrawn.jsx'
import Support from './screens/Support.jsx'
import Paused from './screens/Paused.jsx'

// Vite replaces import.meta.env.DEV with false at build time, so the dev routes
// and the config JSON they import are dead code in a production bundle.
const FormatGallery = import.meta.env.DEV
  ? lazy(() => import('./dev/FormatGallery.jsx'))
  : null
const LayoutPreview = import.meta.env.DEV
  ? lazy(() => import('./dev/LayoutPreview.jsx'))
  : null
// /dev — the index of every unique screen, so reviewing one does not mean
// walking the funnel to reach it. Same gate: absent from a production build.
const DevConsole = import.meta.env.DEV
  ? lazy(() => import('./dev/Console.jsx'))
  : null
const BeepPreview = import.meta.env.DEV
  ? lazy(() => import('./dev/BeepPreview.jsx'))
  : null

/**
 * Route table. This file owns the route names, the redirect on session loss,
 * and nothing else.
 *
 * Onboarding order is fixed and load-bearing (FE-5):
 *   consent -> install -> notification permission
 * Install sits before the permission prompt because on iOS push does not exist
 * until the app is on the home screen, and a permission denial in Safari cannot
 * be re-asked.
 */
export default function App() {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()

  // THE single 401 handler for the whole client. Registered once, here.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      /*
       * DEV ROUTES SURVIVE A SIGNED-OUT STATE. /dev is the screen index and
       * needs no session, but the shell's launch-time calls 401 after a sign
       * out and this GLOBAL handler was bouncing every route to /login -
       * including the one page whose whole job is to hand out new sessions.
       * import.meta.env.DEV is compile-time false in production, so this
       * branch does not exist in a shipped bundle.
       */
      if (import.meta.env.DEV && window.location.pathname.startsWith('/dev')) return
      queryClient.clear()
      const here = location.pathname + location.search
      navigate(`/login?next=${encodeURIComponent(here)}`, { replace: true })
    })
    return () => setUnauthorizedHandler(null)
  }, [navigate, queryClient, location.pathname, location.search])

  // Re-assert the push subscription once per launch. Never prompts: it does
  // nothing unless permission was already granted on the onboarding screen.
  // This is what recovers a participant whose subscription the server dropped
  // on a 410, or whose browser rotated the endpoint.
  useEffect(() => {
    ensurePushSubscription(push.subscribe)
  }, [])

  return (
    <Routes>
      <Route path="/" element={<Home />} />

      <Route path="/register" element={<Register />} />
      <Route path="/login" element={<Login />} />

      {/* This order is the ticket's order and must not be rearranged. */}
      <Route path="/consent" element={<Consent />} />
      <Route path="/install" element={<Install />} />
      <Route path="/notifications" element={<Notifications />} />

      {/* Settings holds the way out of the study: findable in the obvious
          place, never on the beep flow and never on a crisis screen. */}
      <Route path="/settings" element={<Settings />} />

      {/* Reachable at any time by anyone, triggered by nothing. DS-1 part 2
          rec B: the resources being permanently there is what stops an
          interposed safety screen meaning "your answer was the serious one". */}
      <Route path="/support" element={<Support />} />

      {/* Where "Stop for now" lands: the least eventful screen in the app, so
          leaving discloses nothing to whoever picks the phone up next. */}
      <Route path="/paused" element={<Paused />} />
      {/* The ending. Renders with no session, because after withdrawal every
          other endpoint 401s and login 403s. */}
      <Route path="/withdrawn" element={<Withdrawn />} />

      <Route path="/demographics" element={<Demographics />} />

      {/*
        THE TWO ANSWERING SURFACES, AND THE ONLY TWO THAT GET A BOUNDARY.
        A crash here costs a participant real work — up to a 200-item sitting —
        and used to white-screen with no message and no way to know her answers
        had been kept. They had been; every item upserts on its own.

        Deliberately not one boundary at the root: it would catch a crash on
        Settings too and tell her there that her answers are saved, on a screen
        where there are none. A claim wider than its trigger is this project's
        own defect class.

        Separate instances, not one wrapping both, so a crash on a beep cannot
        leave the day-0 route showing a crashed boundary.
      */}
      <Route
        path="/day0"
        element={
          <BatteryBoundary variant="day0">
            <Day0 />
          </BatteryBoundary>
        }
      />
      {/* variant="beep": a beep expires 60 minutes after firing, so day 0's
          "carry on from where you stopped" can already be false by the time she
          reloads. Different truth, not a different tone. */}
      <Route
        path="/beep/:beepId"
        element={
          <BatteryBoundary variant="beep">
            <Beep />
          </BatteryBoundary>
        }
      />

      {/*
        Dev-only proof that the boundary actually catches.

        architecture.md's rule: if you cannot make a check fail on demand it is
        not a check, and a green boundary nobody has watched go red is a claim.
        This route throws on render inside a real BatteryBoundary, so the
        recovery screen can be seen and re-seen rather than reasoned about.

        Gated on import.meta.env.DEV like the other two, so no participant can
        reach a route whose only purpose is to crash.
      */}
      {import.meta.env.DEV && (
        <Route
          path="/dev/crash"
          element={
            <BatteryBoundary variant="day0">
              <CrashOnRender />
            </BatteryBoundary>
          }
        />
      )}

      {/*
        The same proof for the ROOT boundary: deliberately NOT wrapped, so the
        throw falls past every inner boundary to the one in main.jsx. This is
        also what proves the two are distinct — it must show the root copy and
        must NOT claim her answers are saved, because on this path there may be
        no answers at all.
      */}
      {import.meta.env.DEV && (
        <Route
          path="/dev/crash-beep"
          element={
            <BatteryBoundary variant="beep">
              <CrashOnRender />
            </BatteryBoundary>
          }
        />
      )}

      {import.meta.env.DEV && <Route path="/dev/crash-root" element={<CrashOnRender />} />}

      {BeepPreview && (
        <Route
          path="/dev/beep"
          element={
            <Suspense fallback={null}>
              <BeepPreview />
            </Suspense>
          }
        />
      )}

      {DevConsole && (
        <Route
          path="/dev"
          element={
            <Suspense fallback={null}>
              <DevConsole />
            </Suspense>
          }
        />
      )}

      {/* Dev-only proof for FE-2: every format in config, on one page. */}
      {FormatGallery && (
        <Route
          path="/dev/formats"
          element={
            <Suspense fallback={null}>
              <FormatGallery />
            </Suspense>
          }
        />
      )}
      {/* Dev-only proof for FE-3: grid alignment, stack paging, safety block. */}
      {LayoutPreview && (
        <Route
          path="/dev/layout"
          element={
            <Suspense fallback={null}>
              <LayoutPreview />
            </Suspense>
          }
        />
      )}

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
